import type { InputNumberRef } from '@rc-component/input-number';
import { Select, Tooltip } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import {
  FARM_DIAGNOSTICS_PRACTICE_LABELS,
  FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL,
  FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL,
  FARM_YEAR_INPUTS_COLLAPSED_HINT,
  FARM_YEAR_INPUTS_COPY_FROM_ABOVE_LABEL,
  FARM_YEAR_INPUTS_COPY_FROM_ABOVE_TOOLTIP,
  FARM_YEAR_INPUTS_PANEL_TITLE,
  FARM_YEAR_INPUTS_PRACTICE_LABEL,
  FARM_YEAR_INPUTS_PRACTICE_SELECT_PLACEHOLDER,
  FARM_YEAR_INPUTS_RAINFALL_LABEL,
  FARM_YEAR_INPUTS_RAINFALL_REQUIRED_TOOLTIP,
} from 'constants/uiConstants';
import React, { useRef } from 'react';
import { LuChevronDown, LuChevronUp, LuCopy } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import {
  setFarmYearInputsExpanded,
  type PracticeTag,
} from '../../../../redux/slices/InsightsSlice';
import SectionCard from '../Insights/summary2/SectionCard';
import { resolvePracticeTagsAfterChange } from './computeFarmDiagnosticsMetrics';
import { useFarmYearInputActions } from './useFarmYearInputActions';

const PRACTICE_OPTIONS = (
  Object.keys(FARM_DIAGNOSTICS_PRACTICE_LABELS) as PracticeTag[]
).map((value) => ({ value, label: FARM_DIAGNOSTICS_PRACTICE_LABELS[value] }));

interface FarmYearInputsPanelProps {
  farmId: string;
  years: number[];
  /**
   * Appended to the panel title (e.g. "Farm-Level Inputs — Aussie") when
   * more than one of these panels can be on screen at once — Rotation
   * Efficiency at Org scope renders one per farm present, and without a
   * name they'd be visually indistinguishable. Omitted by the per-paddock
   * lenses, which only ever render a single instance for the one farm the
   * selected paddock belongs to.
   */
  farmName?: string;
  /**
   * The immediately preceding farm's id, when this panel is one of several
   * stacked together (Rotation Efficiency at Org scope) — enables "Copy
   * from above", which fills this farm's rainfall/practice from that farm's
   * own entries for any matching year. Omitted (no button shown) for the
   * first panel in a stack, or when there's only ever one panel (the
   * per-paddock lenses).
   */
  previousFarmId?: string;
}

/**
 * Farm-level rainfall + management-practice tags, keyed by `farmId` (not
 * `paddockId`) — this panel is deliberately not nested inside any
 * paddock-specific component, since the same entries apply regardless of
 * which paddock the tree currently has selected. Shared by every
 * per-paddock lens (rendered once, above the Yearly Overview table).
 *
 * `years` is the full contiguous calendar-year range spanned by the
 * currently selected time window — not just years that happen to have
 * Epic 1 crop data — so a true gap year (e.g. Fallow, which by definition
 * has no Input document since nothing was planted) can still be tagged.
 *
 * Practice is a fixed, controlled multi-select (never free text — a new
 * tag value would silently break Dashboard 4's legend/correlation logic).
 * Fallow/Pasture are mutually exclusive with each other and with every
 * active-cropping practice, enforced via resolvePracticeTagsAfterChange.
 *
 * Expanded by default, like the Yearly Overview table — both need the
 * user's own rainfall and practice input. Entries are remembered in this
 * browser (see useFarmYearInputActions).
 */
const FarmYearInputsPanel: React.FC<FarmYearInputsPanelProps> = ({
  farmId,
  years,
  farmName,
  previousFarmId,
}) => {
  const dispatch = useDispatch();
  const { setRainfall, setPracticeTags } = useFarmYearInputActions(farmId);
  const farmYearInputs = useSelector(
    (state: RootState) => state.insightsReducer.farmYearInputs[farmId]
  );
  const previousFarmYearInputs = useSelector((state: RootState) =>
    previousFarmId ? state.insightsReducer.farmYearInputs[previousFarmId] : undefined
  );
  // Redux, not local state — this panel is a different component instance
  // per Farm Diagnostics lens branch, so local state would reset every time
  // the user switches lenses. Absent key = expanded (the default): this
  // panel needs the user's own rainfall/practice input.
  const expanded = useSelector(
    (state: RootState) => state.insightsReducer.farmYearInputsExpanded[farmId] ?? true
  );
  // Indexed by row position — lets ArrowUp/ArrowDown move focus to the
  // adjacent row's rainfall field; the onKeyDown on each NumericInputField
  // uses this ref array to navigate. keyboard={false} stops the input
  // also stepping its value by 1 on the same key press.
  const rainfallInputRefs = useRef<(InputNumberRef | null)[]>([]);

  if (years.length === 0) return null;

  // Only offered when the previous farm actually has something to copy —
  // matches this feature's existing "only show an action when there's
  // something to act on" convention (e.g. Reset All).
  const hasDataToCopy =
    !!previousFarmYearInputs &&
    years.some((year) => {
      const source = previousFarmYearInputs[year];
      return source && (source.rainfallMm != null || source.practiceTags.length > 0);
    });

  const handleCopyFromAbove = () => {
    if (!previousFarmYearInputs) return;
    for (const year of years) {
      const source = previousFarmYearInputs[year];
      if (!source) continue;
      if (source.rainfallMm != null) {
        setRainfall(year, source.rainfallMm);
      }
      if (source.practiceTags.length > 0) {
        setPracticeTags(year, source.practiceTags);
      }
    }
  };

  return (
    <SectionCard
      id={`farmDiagnostics-farmYearInputs-${farmId}`}
      title={farmName ? `${FARM_YEAR_INPUTS_PANEL_TITLE} — ${farmName}` : FARM_YEAR_INPUTS_PANEL_TITLE}
      variant="config"
      collapsed={!expanded}
      actions={
        <>
          {expanded && hasDataToCopy && (
            <Tooltip title={FARM_YEAR_INPUTS_COPY_FROM_ABOVE_TOOLTIP}>
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
                onClick={handleCopyFromAbove}
              >
                <LuCopy size={14} />
                {FARM_YEAR_INPUTS_COPY_FROM_ABOVE_LABEL}
              </button>
            </Tooltip>
          )}
          {/* RH side, consistent with the Yearly Overview section below. */}
          <button
            type="button"
            className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
            onClick={() =>
              dispatch(setFarmYearInputsExpanded({ farmId, expanded: !expanded }))
            }
          >
            {expanded ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
            {expanded
              ? FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL
              : FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL}
          </button>
        </>
      }
    >
      {/* text-gray-dark, not gray-400 — this is the only visible content while collapsed, and gray-400 falls under WCAG AA contrast. */}
      {!expanded ? (
        <span className="text-sm text-gray-dark">{FARM_YEAR_INPUTS_COLLAPSED_HINT}</span>
      ) : (
        <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
          <table className="w-max border-collapse text-base">
            <thead>
              <tr>
                <th className="w-16 pb-2 pl-2 pr-4 text-left text-sm font-bold text-charcoal">
                  Year
                </th>
                <th className="w-40 px-2 pb-2 text-left text-sm font-bold text-charcoal">
                  <Tooltip title={FARM_YEAR_INPUTS_RAINFALL_REQUIRED_TOOLTIP}>
                    <span className="cursor-help">
                      {FARM_YEAR_INPUTS_RAINFALL_LABEL} <span className="text-danger">*</span>
                    </span>
                  </Tooltip>
                </th>
                <th className="px-2 pb-2 text-left text-sm font-bold text-charcoal">
                  {FARM_YEAR_INPUTS_PRACTICE_LABEL}
                </th>
              </tr>
            </thead>
            <tbody>
              {years.map((year, idx) => {
                const rainfallMm = farmYearInputs?.[year]?.rainfallMm ?? null;
                const practiceTags = farmYearInputs?.[year]?.practiceTags ?? [];
                return (
                  <tr
                    key={year}
                    className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="w-16 py-1.5 pl-2 pr-4 text-base font-semibold text-charcoal">
                      {year}
                    </td>
                    <td className="w-40 px-2 py-1.5">
                      <NumericInputField
                        ref={(el) => {
                          rainfallInputRefs.current[idx] = el;
                        }}
                        min={0}
                        controls={false}
                        keyboard={false}
                        unit="mm"
                        unitClassName="-ml-2 self-stretch flex items-center rounded-r border border-l-0 border-gray-300 bg-gray-50 px-2 text-sm text-gray-muted"
                        inputClassName="w-20 rounded-r-none"
                        value={rainfallMm}
                        onChange={(value) => setRainfall(year, value)}
                        onKeyDown={(event) => {
                          if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            rainfallInputRefs.current[idx - 1]?.focus();
                          } else if (event.key === 'ArrowDown') {
                            event.preventDefault();
                            rainfallInputRefs.current[idx + 1]?.focus();
                          }
                        }}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Select
                        mode="multiple"
                        size="small"
                        className="w-64"
                        placeholder={FARM_YEAR_INPUTS_PRACTICE_SELECT_PLACEHOLDER}
                        value={practiceTags}
                        options={PRACTICE_OPTIONS}
                        onChange={(newSelection: PracticeTag[]) =>
                          setPracticeTags(
                            year,
                            resolvePracticeTagsAfterChange(practiceTags, newSelection)
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

export default FarmYearInputsPanel;
